import type { CalendarEventExternal } from '@schedule-x/calendar';
import type { Temporal } from 'temporal-polyfill';
import type { MaybeRefOrGetter } from 'vue';
import type { FormatLocale } from '#layers/base/public';
import type { ScheduleXView } from '#layers/listing/constants/calendar';
import type { CalendarRange } from './build-calendar-range-filter';
import { createCalendar, createViewDay, createViewMonthGrid, createViewWeek } from '@schedule-x/calendar';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { computed, readonly, ref, shallowRef, toValue, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CALENDAR_TIME_GRID_HEIGHT } from '#layers/listing/constants/calendar';
import { buildScheduleXTranslations, toScheduleXLocale } from './schedule-x-locale';

interface UseScheduleXCalendarOptions {
	view: MaybeRefOrGetter<ScheduleXView>;
	date: MaybeRefOrGetter<Temporal.PlainDate>;
	weekStartsOn: MaybeRefOrGetter<1 | 7>;
	timeZone: MaybeRefOrGetter<string>;
	locale: MaybeRefOrGetter<FormatLocale>;
	onEventClick: (calendarEvent: CalendarEventExternal) => void;
	onClickPlusEvents: (date: Temporal.PlainDate) => void;
}

export function useScheduleXCalendar({ view, date, weekStartsOn, timeZone, locale, onEventClick, onClickPlusEvents }: UseScheduleXCalendarOptions) {
	const { t } = useI18n();

	const scheduleXLocale = computed(() => toScheduleXLocale(toValue(locale)));

	// build() replaces this placeholder with schedule-x's range right away.
	const range = shallowRef<CalendarRange>({ start: toValue(date), end: toValue(date) });

	const scheduleX = shallowRef(build());
	const calendarKey = ref(0);

	function setRange(next: { start: Temporal.ZonedDateTime; end: Temporal.ZonedDateTime }) {
		range.value = { start: next.start.toPlainDate(), end: next.end.toPlainDate() };
	}

	function build() {
		const controls = createCalendarControlsPlugin();

		const app = createCalendar({
			views: [createViewMonthGrid(), createViewWeek(), createViewDay()],
			defaultView: toValue(view),
			firstDayOfWeek: toValue(weekStartsOn),
			timezone: toValue(timeZone),
			locale: scheduleXLocale.value,
			translations: buildScheduleXTranslations(t, scheduleXLocale.value),
			monthGridOptions: { nEventsPerDay: 3 },
			skipAnimations: true,
			weekOptions: { gridHeight: CALENDAR_TIME_GRID_HEIGHT, eventOverlap: false },
			// Stays off, because a responsive calendar reads the hidden grid's zero width and switches views.
			isResponsive: false,
			selectedDate: toValue(date),
			events: [],
			callbacks: {
				// schedule-x never reports its first range, so build() reads it from the controls below.
				onRangeUpdate: setRange,
				onEventClick,
				// Stops schedule-x from switching to Day on its own.
				onClickPlusEvents,
			},
		}, [controls]);

		const initialRange = controls.getRange();
		if (initialRange) setRange(initialRange);

		return { app, controls };
	}

	// Rebuilds the app before other watchers run, because schedule-x keeps the range and translations it was created with.
	watch(
		[() => toValue(weekStartsOn), () => toValue(timeZone), scheduleXLocale],
		() => {
			scheduleX.value = build();
			calendarKey.value++;
		},
		{ flush: 'sync' },
	);

	watch(() => toValue(date), (next) => scheduleX.value.controls.setDate(next));
	watch(() => toValue(view), (next) => scheduleX.value.controls.setView(next));

	return {
		calendarApp: computed(() => scheduleX.value.app),
		calendarKey: readonly(calendarKey),
		range: computed(() => range.value),
	};
}
